/**
 * Device Store
 *
 * Zustand store for ESP32 device management.
 * Handles CRUD operations and real-time status updates via Socket.IO.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { io, Socket } from 'socket.io-client';
import { defaultWorkflowConfig } from '@/types/workflow-config';
import { useAuthStore } from '@/stores/auth/auth-store';
import { buildWorkflowUserApiRoute } from '@/utils/workflow-utils';
import { workflowFetch } from '@/services/api/workflowApi';

// Device types
export interface DeviceCapabilities {
  has_display: boolean;
  has_touch: boolean;
  gpio_count: number;
  has_wifi: boolean;
  has_bluetooth?: boolean;
  has_ble?: boolean;
  flash_size_mb?: number;
  psram_size_mb?: number;
  gpio_map?: number[];
}

export type DeviceStatus = 'online' | 'offline' | 'deploying' | 'running' | 'error';
export type DeviceType = 'esp32';

export interface PendingDeployment {
  workflowId: string;
  queuedAt: string;
  queuedBy: string;
}

export interface Device {
  id: string;
  _id?: string;
  deviceId: string;
  name: string;
  type: DeviceType;
  capabilities: DeviceCapabilities;
  firmwareVersion?: string;
  lastSeen: string;
  status: DeviceStatus;
  currentWorkflowId?: string;
  currentWorkflow?: {
    id: string;
    name: string;
    status: string;
  };
  pendingDeployment?: PendingDeployment;
  metadata?: {
    location?: string;
    description?: string;
    tags?: string[];
  };
  integrationMeta?: {
    relayState?: 'on' | 'off';
    brightness?: number;
    [key: string]: unknown;
  };
  // Deployment tracking
  lastDeploymentSuccess?: boolean;
  lastDeploymentError?: string | null;
}

export interface DeviceCounts {
  total: number;
  online: number;
  offline: number;
  deploying: number;
  running: number;
  error: number;
}

// Execution log entry types
export interface ExecutionStep {
  deviceId?: string;      // ESP32 device execution
  executionId?: string;   // Server workflow execution
  workflowId: string;
  nodeId: string;
  nodeType: string;
  status: 'started' | 'completed' | 'error' | 'skipped';
  message?: string;
  data?: Record<string, unknown>;
  timestamp: number;
}

export interface ExecutionLog {
  deviceId: string;
  workflowId?: string;
  level: 'debug' | 'info' | 'warn' | 'error';
  message: string;
  data?: Record<string, unknown>;
  timestamp: number;
}

// Device telemetry from ESP32 heartbeat
export interface DeviceTelemetry {
  deviceId: string;
  freeHeap: number;
  minFreeHeap: number;
  uptime: number;
  rssi: number;
  firmwareVersion: string;
  cpuFreqMHz: number;
  workflowState: string;
  currentScreen: string;
  loggingEnabled: boolean;
  timestamp: string;
}

interface DeviceState {
  // Data
  devices: Device[];
  counts: DeviceCounts;
  selectedDevice: Device | null;

  // Telemetry data (keyed by deviceId)
  telemetry: Map<string, DeviceTelemetry>;

  // Execution logs (keyed by deviceId)
  executionSteps: Map<string, ExecutionStep[]>;
  executionLogs: Map<string, ExecutionLog[]>;

  // Socket
  socket: Socket | null;
  isSocketConnected: boolean;

  // Loading states
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;

  // Actions
  initialize: () => Promise<void>;
  fetchDevices: () => Promise<void>;
  getDevice: (deviceId: string) => Promise<Device | null>;
  registerDevice: (data: Partial<Device>) => Promise<Device | null>;
  updateDevice: (deviceId: string, data: Partial<Device>) => Promise<Device | null>;
  deleteDevice: (deviceId: string) => Promise<boolean>;
  deployWorkflow: (deviceId: string, workflowId: string) => Promise<{ success: boolean; queued?: boolean }>;
  stopWorkflow: (deviceId: string) => Promise<boolean>;
  getOnlineDevices: () => Promise<Device[]>;

  // Execution log actions (key = deviceId for ESP32, executionId for server workflows)
  getExecutionSteps: (key: string) => ExecutionStep[];
  getExecutionLogs: (key: string) => ExecutionLog[];
  clearExecutionLogs: (key: string) => void;

  // Telemetry actions
  getTelemetry: (deviceId: string) => DeviceTelemetry | undefined;
  setDeviceLogging: (deviceId: string, enabled: boolean) => Promise<boolean>;
  restartDevice: (deviceId: string) => Promise<boolean>;

  // Filter options for device_data trigger
  dataFilterOptions: {
    dataTypes: string[];
    esp32WorkflowIds: string[];
    customIds: string[];
    esp32Workflows: Array<{ id: string; name: string; status: string }>;
  };
  isLoadingFilterOptions: boolean;
  filterOptionsLoaded: boolean;
  fetchDataFilterOptions: () => Promise<void>;

  // Workflow subscription for server debug mode
  subscribedWorkflows: Set<string>;
  subscribeToWorkflow: (workflowId: string) => void;
  unsubscribeFromWorkflow: (workflowId: string) => void;

  // Internal
  setSelectedDevice: (device: Device | null) => void;
  cleanup: () => void;
}

const WORKFLOW_API_BASE = defaultWorkflowConfig.workflowEngineUrl;

// Helper to get user ID
const getUserId = () => {
  const user = useAuthStore.getState().user;
  return user?._id || user?.id;
};

export const useDeviceStore = create<DeviceState>()(
  devtools(
    immer((set, get) => ({
      // Initial state
      devices: [],
      counts: { total: 0, online: 0, offline: 0, deploying: 0, running: 0, error: 0 },
      selectedDevice: null,
      telemetry: new Map(),
      executionSteps: new Map(),
      executionLogs: new Map(),
      socket: null,
      isSocketConnected: false,
      isLoading: false,
      isInitialized: false,
      error: null,
      dataFilterOptions: {
        dataTypes: [],
        esp32WorkflowIds: [],
        customIds: [],
        esp32Workflows: [],
      },
      isLoadingFilterOptions: false,
      filterOptionsLoaded: false,
      subscribedWorkflows: new Set(),

      // Initialize store and socket connection
      initialize: async () => {
        const { isInitialized, isLoading, socket } = get();
        console.log('[Device Store] initialize() called', { isInitialized, isLoading, hasSocket: !!socket });
        if (isInitialized || isLoading) {
          console.log('[Device Store] Skipping initialization - already initialized or loading');
          return;
        }

        set({ isLoading: true, error: null });

        try {
          // Get auth token for WebSocket connection
          const authState = useAuthStore.getState();
          const accessToken = authState.tokens?.accessToken;
          console.log('[Device Store] Creating socket connection to:', WORKFLOW_API_BASE, 'hasToken:', !!accessToken);

          // Connect to Socket.IO for real-time updates
          if (!socket) {
            // Parse URL to extract path prefix for proxy routing
            // Socket.IO interprets URL path as namespace, so we must use origin only
            // and put the proxy prefix in the path option
            const urlObj = /^https?:\/\//i.test(WORKFLOW_API_BASE)
              ? new URL(WORKFLOW_API_BASE)
              : new URL(WORKFLOW_API_BASE, window.location.origin);
            const pathPrefix = urlObj.pathname.replace(/\/$/, '');
            const socketPath = pathPrefix ? `${pathPrefix}/socket.io` : '/socket.io';

            const newSocket = io(urlObj.origin, {
              path: socketPath,
              transports: ['websocket', 'polling'],
              autoConnect: true,
              auth: {
                token: accessToken,
              },
            });

            newSocket.on('connect', () => {
              set({ isSocketConnected: true });
              console.log('Device store: Socket.IO connected');
            });

            newSocket.on('disconnect', (reason) => {
              set({ isSocketConnected: false });
              console.log('Device store: Socket.IO disconnected, reason:', reason);
            });

            newSocket.on('connect_error', (error) => {
              console.error('Device store: Socket.IO connection error:', error.message);
            });

            // Listen for device status changes (including deployment acknowledgments)
            newSocket.on('device:status:changed', (data: {
              deviceId: string;
              status: DeviceStatus;
              workflowId?: string;
              deploymentSuccess?: boolean;
              error?: string | null;
              timestamp?: string;
            }) => {
              console.log('Device status changed:', data);
              set((state) => {
                const deviceIndex = state.devices.findIndex((d) => d.deviceId === data.deviceId);
                if (deviceIndex !== -1) {
                  const oldStatus = state.devices[deviceIndex].status;
                  const newStatus = data.status;

                  // Update device fields
                  state.devices[deviceIndex].status = newStatus;

                  if (data.workflowId !== undefined) {
                    state.devices[deviceIndex].currentWorkflowId = data.workflowId;
                  }
                  if (data.deploymentSuccess !== undefined) {
                    state.devices[deviceIndex].lastDeploymentSuccess = data.deploymentSuccess;
                  }
                  if (data.error !== undefined) {
                    state.devices[deviceIndex].lastDeploymentError = data.error;
                  }

                  // Update counts if status changed
                  if (oldStatus !== newStatus) {
                    if (oldStatus && state.counts[oldStatus] !== undefined) {
                      state.counts[oldStatus] = Math.max(0, state.counts[oldStatus] - 1);
                    }
                    if (newStatus && state.counts[newStatus] !== undefined) {
                      state.counts[newStatus] += 1;
                    }
                  }
                }
              });
            });

            // Listen for workflow deployment status
            newSocket.on('device:deploy:status', (data: { deviceId: string; workflowId: string; status: string }) => {
              console.log('Deployment status:', data);
            });

            // Kasa relay state change — optimistic update pushed by the workflow
            // server immediately after a successful control command. Lets the UI
            // reflect on/off state without waiting for the next Pi poll cycle.
            newSocket.on('kasa:relay:changed', (data: {
              deviceId: string;
              host?: string;
              childId?: string | null;
              relayState: 'on' | 'off';
              timestamp?: string;
            }) => {
              set((state) => {
                const idx = state.devices.findIndex((d) => d.deviceId === data.deviceId);
                const integrationMeta = idx !== -1 ? state.devices[idx].integrationMeta : undefined;
                if (integrationMeta) {
                  integrationMeta.relayState = data.relayState;
                }
              });
            });

            // Kasa brightness change — optimistic push for dimmer sliders.
            newSocket.on('kasa:brightness:changed', (data: {
              deviceId: string;
              host?: string;
              childId?: string | null;
              brightness: number;
              timestamp?: string;
            }) => {
              set((state) => {
                const idx = state.devices.findIndex((d) => d.deviceId === data.deviceId);
                const integrationMeta = idx !== -1 ? state.devices[idx].integrationMeta : undefined;
                if (integrationMeta) {
                  integrationMeta.brightness = data.brightness;
                }
              });
            });

            // Listen for workflow completion
            newSocket.on('device:workflow:complete', (data: { deviceId: string; workflowId: string; success: boolean }) => {
              set((state) => {
                const deviceIndex = state.devices.findIndex((d) => d.deviceId === data.deviceId);
                if (deviceIndex !== -1) {
                  state.devices[deviceIndex].status = 'online';
                  if (!data.success) {
                    state.devices[deviceIndex].currentWorkflowId = undefined;
                  }
                }
              });
            });

            // Listen for execution step updates from ESP32
            newSocket.on('device:execution:step', (data: ExecutionStep) => {
              console.log('Execution step (ESP32):', data);
              set((state) => {
                const key = data.deviceId || data.executionId || 'unknown';
                const steps = state.executionSteps.get(key) || [];
                // Keep only last 100 steps per device/execution
                const newSteps = [...steps, data].slice(-100);
                state.executionSteps.set(key, newSteps);
              });
            });

            // Listen for execution step updates from server workflows (debug mode)
            newSocket.on('workflow:execution:step', (data: ExecutionStep) => {
              console.log('Execution step (Server):', data);
              set((state) => {
                const key = data.executionId || data.deviceId || 'unknown';
                const steps = state.executionSteps.get(key) || [];
                // Keep only last 100 steps per execution
                const newSteps = [...steps, data].slice(-100);
                state.executionSteps.set(key, newSteps);
              });
            });

            // Listen for execution log messages from ESP32
            newSocket.on('device:execution:log', (data: ExecutionLog) => {
              console.log('Execution log:', data);
              set((state) => {
                const logs = state.executionLogs.get(data.deviceId) || [];
                // Keep only last 200 logs per device
                const newLogs = [...logs, data].slice(-200);
                state.executionLogs.set(data.deviceId, newLogs);
              });
            });

            // Listen for device telemetry (heartbeat every 15 seconds)
            newSocket.on('device:telemetry', (data: DeviceTelemetry) => {
              console.log('📡 Device telemetry received:', data.deviceId, data);
              set((state) => {
                // Create new Map to trigger re-render (immer doesn't detect Map.set mutations)
                const newTelemetry = new Map(state.telemetry);
                newTelemetry.set(data.deviceId, data);
                state.telemetry = newTelemetry;
              });
            });

            // Listen for pending deployment started (queued deployment executing on reconnect)
            newSocket.on('device:pending_deployment:started', (data: {
              deviceId: string;
              workflowId: string;
              workflowName: string;
              queuedAt: string;
              timestamp: string;
            }) => {
              console.log('📦 Pending deployment started:', data);
              set((state) => {
                const deviceIndex = state.devices.findIndex((d) => d.deviceId === data.deviceId);
                if (deviceIndex !== -1) {
                  state.devices[deviceIndex].status = 'deploying';
                  state.devices[deviceIndex].pendingDeployment = undefined;
                }
              });
            });

            // Listen for pending deployment failed
            newSocket.on('device:pending_deployment:failed', (data: {
              deviceId: string;
              workflowId: string;
              error: string;
              timestamp: string;
            }) => {
              console.log('❌ Pending deployment failed:', data);
              set((state) => {
                const deviceIndex = state.devices.findIndex((d) => d.deviceId === data.deviceId);
                if (deviceIndex !== -1) {
                  state.devices[deviceIndex].pendingDeployment = undefined;
                  state.devices[deviceIndex].lastDeploymentSuccess = false;
                  state.devices[deviceIndex].lastDeploymentError = data.error;
                }
              });
            });

            set({ socket: newSocket });
          }

          // Fetch devices
          await get().fetchDevices();

          set({ isLoading: false, isInitialized: true });
        } catch (error) {
          console.error('Failed to initialize device store:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to initialize',
            isLoading: false,
          });
        }
      },

      // Fetch all devices
      fetchDevices: async () => {
        const userId = getUserId();
        if (!userId) {
          set({ error: 'User not authenticated' });
          return;
        }

        set({ isLoading: true, error: null });

        try {
          const response = await workflowFetch(
            buildWorkflowUserApiRoute('devices', userId),
            {
              headers: { 'Content-Type': 'application/json' },
            },
          );

          if (!response.ok) {
            throw new Error(`Failed to fetch devices: ${response.statusText}`);
          }

          const result = await response.json();
          const devices = result.data?.devices || [];
          const counts = result.data?.counts || { total: 0, online: 0, offline: 0, deploying: 0, error: 0 };

          set({
            devices: devices.map((d: Device) => ({ ...d, id: d._id || d.id })),
            counts,
            isLoading: false,
          });
        } catch (error) {
          console.error('Failed to fetch devices:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch devices',
            isLoading: false,
          });
        }
      },

      // Fetch filter options for device_data trigger configuration
      fetchDataFilterOptions: async () => {
        const { isLoadingFilterOptions, filterOptionsLoaded } = get();

        // Guard: Don't fetch if already loading or already loaded
        if (isLoadingFilterOptions || filterOptionsLoaded) {
          return;
        }

        const userId = getUserId();
        if (!userId) {
          console.warn('User not authenticated, cannot fetch data filter options');
          return;
        }

        set({ isLoadingFilterOptions: true });

        try {
          const response = await workflowFetch(
            buildWorkflowUserApiRoute('devices/data/filter-options', userId),
            {
              headers: { 'Content-Type': 'application/json' },
            },
          );

          if (!response.ok) {
            throw new Error(`Failed to fetch filter options: ${response.statusText}`);
          }

          const result = await response.json();
          const data = result.data || {};

          set({
            dataFilterOptions: {
              dataTypes: data.dataTypes || [],
              esp32WorkflowIds: data.esp32WorkflowIds || [],
              customIds: data.customIds || [],
              esp32Workflows: data.esp32Workflows || [],
            },
            isLoadingFilterOptions: false,
            filterOptionsLoaded: true,
          });
        } catch (error) {
          console.error('Failed to fetch data filter options:', error);
          set({ isLoadingFilterOptions: false });
        }
      },

      // Get single device
      getDevice: async (deviceId: string) => {
        const userId = getUserId();
        if (!userId) return null;

        try {
          const response = await workflowFetch(
            buildWorkflowUserApiRoute(`devices/${deviceId}`, userId),
            {
              headers: { 'Content-Type': 'application/json' },
            },
          );

          if (!response.ok) return null;

          const result = await response.json();
          return result.data;
        } catch (error) {
          console.error('Failed to get device:', error);
          return null;
        }
      },

      // Register new device
      registerDevice: async (data: Partial<Device>) => {
        const userId = getUserId();
        if (!userId) {
          set({ error: 'User not authenticated' });
          return null;
        }

        try {
          const response = await workflowFetch(
            buildWorkflowUserApiRoute('devices', userId),
            {
            method: 'POST',
              headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
            },
          );

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to register device');
          }

          const result = await response.json();
          const newDevice = { ...result.data, id: result.data._id || result.data.id };

          set((state) => {
            state.devices.push(newDevice);
            state.counts.total += 1;
            state.counts.offline += 1;
          });

          return newDevice;
        } catch (error) {
          console.error('Failed to register device:', error);
          set({ error: error instanceof Error ? error.message : 'Failed to register device' });
          return null;
        }
      },

      // Update device
      updateDevice: async (deviceId: string, data: Partial<Device>) => {
        const userId = getUserId();
        if (!userId) return null;

        try {
          const response = await workflowFetch(
            buildWorkflowUserApiRoute(`devices/${deviceId}`, userId),
            {
            method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
            },
          );

          if (!response.ok) {
            throw new Error('Failed to update device');
          }

          const result = await response.json();
          const updatedDevice = { ...result.data, id: result.data._id || result.data.id };

          set((state) => {
            const index = state.devices.findIndex((d) => d.id === deviceId);
            if (index !== -1) {
              state.devices[index] = updatedDevice;
            }
          });

          return updatedDevice;
        } catch (error) {
          console.error('Failed to update device:', error);
          return null;
        }
      },

      // Delete device
      deleteDevice: async (deviceId: string) => {
        const userId = getUserId();
        if (!userId) return false;

        try {
          const response = await workflowFetch(
            buildWorkflowUserApiRoute(`devices/${deviceId}`, userId),
            {
            method: 'DELETE',
              headers: { 'Content-Type': 'application/json' },
            },
          );

          if (!response.ok) {
            throw new Error('Failed to delete device');
          }

          set((state) => {
            const device = state.devices.find((d) => d.id === deviceId);
            if (device) {
              state.counts.total -= 1;
              state.counts[device.status] -= 1;
            }
            state.devices = state.devices.filter((d) => d.id !== deviceId);
          });

          return true;
        } catch (error) {
          console.error('Failed to delete device:', error);
          return false;
        }
      },

      // Deploy workflow to device
      // Returns { success: boolean, queued?: boolean } to indicate deployment status
      deployWorkflow: async (deviceId: string, workflowId: string): Promise<{ success: boolean; queued?: boolean }> => {
        const userId = getUserId();
        if (!userId) return { success: false };

        try {
          const response = await workflowFetch(
            buildWorkflowUserApiRoute(`devices/${deviceId}/deploy`, userId),
            {
            method: 'POST',
              headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ workflowId }),
            },
          );

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to deploy workflow');
          }

          const result = await response.json();
          const wasQueued = result.data?.queued === true;

          set((state) => {
            const device = state.devices.find((d) => d.id === deviceId);
            if (device) {
              if (wasQueued) {
                // Deployment was queued for offline device
                device.pendingDeployment = {
                  workflowId,
                  queuedAt: result.data.queuedAt || new Date().toISOString(),
                  queuedBy: userId,
                };
              } else {
                // Immediate deployment
                device.status = 'deploying';
                device.currentWorkflowId = workflowId;
                device.pendingDeployment = undefined;
              }
            }
          });

          return { success: true, queued: wasQueued };
        } catch (error) {
          console.error('Failed to deploy workflow:', error);
          set({ error: error instanceof Error ? error.message : 'Failed to deploy workflow' });
          return { success: false };
        }
      },

      // Stop workflow on device
      stopWorkflow: async (deviceId: string) => {
        const userId = getUserId();
        if (!userId) return false;

        try {
          const response = await workflowFetch(
            buildWorkflowUserApiRoute(`devices/${deviceId}/stop`, userId),
            {
            method: 'POST',
              headers: { 'Content-Type': 'application/json' },
            },
          );

          if (!response.ok) {
            throw new Error('Failed to stop workflow');
          }

          set((state) => {
            const device = state.devices.find((d) => d.id === deviceId);
            if (device) {
              device.status = 'online';
              device.currentWorkflowId = undefined;
            }
          });

          return true;
        } catch (error) {
          console.error('Failed to stop workflow:', error);
          return false;
        }
      },

      // Get online devices only
      getOnlineDevices: async () => {
        const userId = getUserId();
        if (!userId) return [];

        try {
          const response = await workflowFetch(
            buildWorkflowUserApiRoute('devices/status/online', userId),
            {
              headers: { 'Content-Type': 'application/json' },
            },
          );

          if (!response.ok) return [];

          const result = await response.json();
          return result.data || [];
        } catch (error) {
          console.error('Failed to get online devices:', error);
          return [];
        }
      },

      // Set selected device
      setSelectedDevice: (device: Device | null) => {
        set({ selectedDevice: device });
      },

      // Get execution steps for a device or server execution
      // Key can be deviceId (ESP32) or executionId (server workflow)
      getExecutionSteps: (key: string) => {
        return get().executionSteps.get(key) || [];
      },

      // Get execution logs for a device
      getExecutionLogs: (key: string) => {
        return get().executionLogs.get(key) || [];
      },

      // Clear execution logs for a device or execution
      // Key can be deviceId (ESP32) or executionId (server workflow)
      clearExecutionLogs: (key: string) => {
        set((state) => {
          state.executionSteps.delete(key);
          state.executionLogs.delete(key);
        });
      },

      // Get telemetry for a device
      getTelemetry: (deviceId: string) => {
        return get().telemetry.get(deviceId);
      },

      // Toggle device logging
      setDeviceLogging: async (deviceId: string, enabled: boolean) => {
        const userId = getUserId();
        if (!userId) return false;

        try {
          const response = await workflowFetch(
            buildWorkflowUserApiRoute(`devices/${deviceId}/logging`, userId),
            {
            method: 'POST',
              headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ enabled }),
            },
          );

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to set logging');
          }

          return true;
        } catch (error) {
          console.error('Failed to set device logging:', error);
          set({ error: error instanceof Error ? error.message : 'Failed to set logging' });
          return false;
        }
      },

      // Restart device
      restartDevice: async (deviceId: string) => {
        const userId = getUserId();
        if (!userId) return false;

        try {
          const response = await workflowFetch(
            buildWorkflowUserApiRoute(`devices/${deviceId}/restart`, userId),
            {
            method: 'POST',
              headers: { 'Content-Type': 'application/json' },
            },
          );

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to restart device');
          }

          return true;
        } catch (error) {
          console.error('Failed to restart device:', error);
          set({ error: error instanceof Error ? error.message : 'Failed to restart device' });
          return false;
        }
      },

      // Subscribe to workflow execution events (for server debug mode)
      subscribeToWorkflow: (workflowId: string) => {
        const { socket, subscribedWorkflows, isSocketConnected } = get();
        if (!socket) {
          console.warn('[Device Store] Cannot subscribe - socket not initialized');
          return;
        }
        if (!isSocketConnected) {
          console.warn('[Device Store] Cannot subscribe - socket not connected');
          return;
        }
        if (subscribedWorkflows.has(workflowId)) {
          console.log('[Device Store] Already subscribed to workflow:', workflowId);
          return;
        }

        socket.emit('workflow-subscribe', { workflowId });
        console.log('[Device Store] ✅ Subscribing to workflow:', workflowId);

        set((state) => {
          const newSet = new Set(state.subscribedWorkflows);
          newSet.add(workflowId);
          state.subscribedWorkflows = newSet;
        });
      },

      // Unsubscribe from workflow execution events
      unsubscribeFromWorkflow: (workflowId: string) => {
        const { socket, subscribedWorkflows } = get();
        if (!socket || !subscribedWorkflows.has(workflowId)) {
          return;
        }

        socket.emit('workflow-unsubscribe', { workflowId });
        console.log('[Device Store] Unsubscribing from workflow:', workflowId);

        set((state) => {
          const newSet = new Set(state.subscribedWorkflows);
          newSet.delete(workflowId);
          state.subscribedWorkflows = newSet;
        });
      },

      // Cleanup
      cleanup: () => {
        const { socket } = get();
        if (socket) {
          socket.disconnect();
        }
        set({
          socket: null,
          isSocketConnected: false,
          isInitialized: false,
          telemetry: new Map(),
          executionSteps: new Map(),
          executionLogs: new Map(),
          subscribedWorkflows: new Set(),
        });
      },
    })),
    { name: 'device-store' }
  )
);
